/**
 * @fileoverview ACTIVE DATA FETCHING HOOK FOR COMPANIES LIST
 * @active Used in current Next.js App Router implementation
 *
 * This hook provides the core data fetching functionality for the companies list feature.
 * It handles loading states, error handling, and dispatching normalized company data to the Redux store.
 *
 * Key features:
 * - Determines whether to fetch all companies or companies by list
 * - Manages loading state during data fetching
 * - Normalizes company data before dispatching to store
 * - Provides error handling for API requests
 *
 * This hook is a critical part of the companies data flow where:
 * 1. Components call getCompanies() with filter/sort params
 * 2. The hook manages loading state and invokes the appropriate API
 * 3. Response data is normalized and dispatched to Redux
 * 4. Components receive updated data through selectors
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - Main consumer component
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/index.tsx} - Filter/sort tools
 * @see {@link src/utils/normalizeCompanyData.ts} - Data normalization utility
 * @see {@link src/services/newCompaniesList.service.ts} - API service for companies data
 * @see {@link src/store/newCompaniesList/newCompaniesList.slice.ts} - Redux slice for companies data
 */

// External dependencies
import { useSelector, useDispatch } from "react-redux";

import {
  useCompaniesByListMutation,
  useCompaniesListMutation,
} from "@/services/newCompaniesList.service";
import { newCompanieListActions } from "@/store/newCompaniesList/newCompaniesList.slice";
import newListSelectors from "@/store/newLists/newLists.slectors";
import type { Company } from "@/types/company";
import { normalizeCompanyData } from "@/utils/normalizeCompanyData";

import { useActions } from "./useActions";

// Define types for the API response
interface CompaniesResponse {
  data: Array<Company>;
}

interface ApiError {
  status?: number;
  message?: string;
  data?: unknown;
}

/**
 * Parameter type for companies list requests
 * Contains filtering, sorting, and pagination options
 */
type CompaniesParams = {
  columnsWidth?: number[];
  columns?: string[];
  arrayFilters?: Record<string, unknown>;
  orderBy?: {
    id: string;
    [key: string]: unknown;
  };
  limit?: number;
};

/**
 * Hook for fetching and managing companies list data
 *
 * @returns {Object} Object containing getCompanies function
 * @returns {Function} getCompanies - Function to fetch companies with specified parameters
 *
 * @example
 * // Basic usage in a component
 * const { getCompanies } = useCompaniesListRequest();
 *
 * // Fetch companies with specific parameters
 * useEffect(() => {
 *   getCompanies({ limit: 10, orderBy: { id: 'nameBrand', desc: true } });
 * }, []);
 */
const useCompaniesListRequest = () => {
  const { setIsLoadingCompanies } = useActions() as {
    setIsLoadingCompanies: (loading: boolean) => void;
  };
  const currentList = useSelector(newListSelectors.getCurrentList());

  const [companiesList] = useCompaniesListMutation();
  const [companiesByList] = useCompaniesByListMutation();

  const dispatch = useDispatch();

  /**
   * Fetch companies based on provided parameters
   *
   * @param {CompaniesParams} params - Query parameters for filtering, sorting, and pagination
   * @param {string} [listId] - Optional list ID to fetch companies from a specific list
   * @returns {Promise} Promise that resolves when data is fetched and dispatched
   */
  const getCompanies = (params: CompaniesParams, listId?: string) => {
    const isAllCompanies = (listId || currentList?.id) === "all companies";
    setIsLoadingCompanies(true);

    // Add a safety timeout to ensure loading state is reset if request takes too long
    const loadingTimeout = setTimeout(() => {
      setIsLoadingCompanies(false);
    }, 10000); // 10 seconds max loading time

    const dispatchData = (data: CompaniesResponse) => {
      clearTimeout(loadingTimeout);
      setIsLoadingCompanies(false);
      dispatch(
        newCompanieListActions.setNewCompanies({
          data: data.data.map(normalizeCompanyData),
        })
      );
    };
    const handleError = (error: ApiError) => {
      clearTimeout(loadingTimeout);
      setIsLoadingCompanies(false);
      console.error("Error fetching companies:", error);
    };
    const fetchCompanies = isAllCompanies
      ? companiesList(params).unwrap().then(dispatchData).catch(handleError)
      : companiesByList({ params, key: listId || currentList?.id })
          .unwrap()
          .then(dispatchData)
          .catch(handleError);

    return fetchCompanies;
  };

  return { getCompanies };
};

export default useCompaniesListRequest;
